import axios, { isAxiosError, type AxiosError } from "axios";
import { env } from "../config/envvars";
import { scrubVendorTerms } from "./vendorTerms";
import { useAuthStore } from "../stores/authStore";
import {
  useWorkspaceStore,
  clearWorkspaceStorage,
} from "../stores/workspaceStore";
import { trackBackendHealth } from "./backendHealth";
import { CLIENT_ID, CLIENT_ID_HEADER } from "./clientId";
import { isTokenFresh } from "./auth";
import { create } from "zustand";

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * A sign-in call (`/auth/login`, Google): its 401 means "wrong details",
     * never "refresh the session", and its 440 never opens the re-auth gate.
     */
    skipAuthRefresh?: boolean;
  }
}

/**
 * Auth HTTP status contract:
 * - 401: missing/expired access token → attempt refresh (never ends session)
 * - 402: monthly AI budget exhausted → stay logged in, surface message
 * - 403: authenticated but not allowed → stay logged in
 * - 440: ONLY status that ends the session → clear tokens and go to login
 */
export const SESSION_EXPIRED_STATUS = 440;
export const AI_BUDGET_EXCEEDED_STATUS = 402;
export const AI_BUDGET_EXCEEDED_CODE = "AI_BUDGET_EXCEEDED";

export type AiBudgetExceededPayload = {
  message?: string;
  code?: string;
  limitUsd?: number;
  spentUsd?: number;
  remainingUsd?: number;
  resetsAt?: string;
};

/** User-facing copy when monthly AI budget is exhausted. */
export function formatAiBudgetExceededMessage(
  data?: AiBudgetExceededPayload | null,
): string {
  const day = data?.resetsAt?.slice(0, 10);
  if (day) {
    return `You've reached your monthly AI usage limit. It resets on ${day}.`;
  }
  return (
    data?.message ??
    "You've reached your monthly AI usage limit. Try again when it resets next month."
  );
}

/**
 * Streaming chat POSTs use responseType:"stream", so a 402 body's JSON may
 * arrive as a ReadableStream / string instead of a parsed object. Hydrate it
 * so callers can read `.message` / `.code`.
 */
async function hydrateAxiosErrorBody(error: AxiosError): Promise<void> {
  const response = error.response;
  if (!response) return;

  let data: unknown = response.data;
  try {
    if (typeof ReadableStream !== "undefined" && data instanceof ReadableStream) {
      data = JSON.parse(await new Response(data).text());
    } else if (typeof Blob !== "undefined" && data instanceof Blob) {
      data = JSON.parse(await data.text());
    } else if (typeof data === "string" && data.trim().startsWith("{")) {
      data = JSON.parse(data);
    }
  } catch {
    return;
  }

  response.data = data;
}

export function isAiBudgetExceededError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const data = error.response?.data as AiBudgetExceededPayload | undefined;
  return (
    error.response?.status === AI_BUDGET_EXCEEDED_STATUS ||
    data?.code === AI_BUDGET_EXCEEDED_CODE
  );
}

/** Prefer budget copy, then API message, then Error.message. */
export function getAiRequestErrorMessage(
  error: unknown,
  fallback = "Something went wrong. Please try again.",
): string {
  if (isAiBudgetExceededError(error)) {
    return formatAiBudgetExceededMessage(
      (error as AxiosError).response?.data as AiBudgetExceededPayload,
    );
  }
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return scrubVendorTerms(data.message);
    // Avoid surfacing axios's generic "Request failed with status code N".
    if (
      error.message &&
      !/^Request failed with status code \d+$/.test(error.message)
    ) {
      return scrubVendorTerms(error.message);
    }
  }
  if (error instanceof Error && error.message) return scrubVendorTerms(error.message);
  return fallback;
}

export const axiosInstance = axios.create({
  baseURL: env.BACKEND_URL + env.API_POSTFIX,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use((config) => {
  const { token } = useAuthStore.getState();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Lets the backend leave this tab out of the realtime broadcast for the
  // change this request makes (lib/clientId.ts).
  config.headers[CLIENT_ID_HEADER] = CLIENT_ID;
  return config;
});

// Every settled backend call feeds the outage guard: a success clears the
// failure streak, an unreachable-type failure extends it, and past
// MAX_BACKEND_RETRIES in a row the app swaps to the "Something went wrong"
// page (see lib/backendHealth.ts). Plain axios calls (SSE chat, uploads) are
// counted here too, not only react-query ones.
trackBackendHealth(axiosInstance);

/**
 * Send one request that has to outlive the page: a tab closing, or a page
 * being hidden for good. A keepalive `fetch` to the URL axiosInstance would
 * build (its own getUri, so the same base URL), carrying the same headers the
 * request interceptor above adds. Fire and forget: there is no refresh on a
 * 401 and no one left to read the answer.
 */
export function sendKeepaliveRequest(method: "DELETE" | "POST" | "PUT", url: string): void {
  const { token } = useAuthStore.getState();
  const headers: Record<string, string> = { [CLIENT_ID_HEADER]: CLIENT_ID };
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    void fetch(axiosInstance.getUri({ url }), { method, headers, keepalive: true }).catch(
      () => {},
    );
  } catch {
    // A page already torn down can refuse to start a fetch; nothing to do.
  }
}

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: string | PromiseLike<string>) => void;
  reject: (reason?: unknown) => void;
}[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token ?? "");
    }
  });
  failedQueue = [];
};

/** Why a session ended, from the 440 body (backend SessionEndReason). */
export type SessionEndReason = "expired" | "revoked" | "locked";

/** The stale access token's age in days: how long the person was away. */
function daysAway(token: string | null): number | null {
  if (!token) return null;
  try {
    const { iat } = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))) as { iat?: number };
    return iat ? Math.max(1, Math.round((Date.now() - iat * 1000) / 86_400_000)) : null;
  } catch {
    return null;
  }
}

/**
 * Ends the session on this device and goes to Sign in with the reason line and
 * the whole destination, so signing in lands where the person was (journey 7).
 */
export const endSession = (reason?: SessionEndReason) => {
  const authState = useAuthStore.getState();
  const away = daysAway(authState.token);
  authState.logout();
  useWorkspaceStore.getState().setSelectedOrganizationId(null);
  useWorkspaceStore.getState().setSelectedBrandId(null);
  clearWorkspaceStorage();
  const params = new URLSearchParams();
  if (reason) params.set("reason", reason);
  if (reason === "expired" && away) params.set("away", String(away));
  const here = window.location.pathname + window.location.search;
  if (here.startsWith("/dashboard") || here.startsWith("/connect")) params.set("redirect", here);
  const query = params.toString();
  window.location.replace(`/login${query ? `?${query}` : ""}`);
};

/** Withdraws this device's refresh token on the server. Fire and forget. */
export function signOutOnServer(): void {
  const { refreshToken } = useAuthStore.getState();
  if (!refreshToken) return;
  void axiosInstance.post("/auth/logout", { refreshToken }, { skipAuthRefresh: true }).catch(() => {});
}

/* ---------------------------------------------------------------------------
 * The re-auth gate (Figma 10 · Session 01, M03). When a session ends while the
 * page has already loaded (work may be open), nothing is cleared: the request
 * that failed waits here, the dialog asks for the password over the page, and
 * signing in re-sends it. A cold load with an ended session goes to Sign in
 * instead (D7), since nothing on screen could be lost.
 * ------------------------------------------------------------------------- */

interface SessionGateState {
  open: boolean;
  reason: SessionEndReason;
}

export const useSessionGate = create<SessionGateState>(() => ({ open: false, reason: "expired" }));

let pageHasAnswered = false;
let gateWaiters: { resolve: (token: string) => void; reject: (e: unknown) => void }[] = [];

function waitForReauth(reason: SessionEndReason): Promise<string> {
  useSessionGate.setState({ open: true, reason });
  return new Promise<string>((resolve, reject) => gateWaiters.push({ resolve, reject }));
}

/** The dialog signed in: store the new pair and let every waiting request go again. */
export function completeReauth(token: string, refreshToken?: string) {
  useAuthStore.getState().login(token, refreshToken ?? "");
  useSessionGate.setState({ open: false });
  const waiters = gateWaiters;
  gateWaiters = [];
  waiters.forEach((w) => w.resolve(token));
}

/** The dialog's Sign out: the waiting requests fail and the session ends here. */
export function abandonReauth() {
  signOutOnServer();
  useSessionGate.setState({ open: false });
  const waiters = gateWaiters;
  gateWaiters = [];
  waiters.forEach((w) => w.reject(new Error("Signed out")));
  endSession();
}

const REFRESH_LOCK = "mohtawa:auth-refresh";

/**
 * Serializes token refreshes across tabs. A refresh token is single-use, so two
 * tabs spending the same one means the loser gets 440 and the session ends.
 * Every tab's socket reconnects the moment the access token expires, which
 * makes that race routine rather than rare. `isRefreshing` only covers this tab.
 */
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator === "undefined" || !navigator.locks) return fn();
  // request() resolves with the callback's settled value; the DOM typing
  // wraps it in a second Promise.
  return navigator.locks.request(REFRESH_LOCK, fn) as unknown as Promise<T>;
}

const refreshToken = async (token: string, refreshToken: string) => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    const newToken = await withRefreshLock(async () => {
      // Tabs don't share in-memory state: re-read storage in case another tab
      // rotated the pair while this one waited, and adopt its result rather
      // than spend a refresh token that is already used.
      await useAuthStore.persist.rehydrate();
      const latest = useAuthStore.getState();
      if (latest.token && latest.token !== token && isTokenFresh(latest.token)) {
        return latest.token;
      }

      const response = await axiosInstance<{
        token: string;
        refreshToken: string;
      }>({
        method: "POST",
        url: "auth/refresh-token",
        data: {
          token: latest.token ?? token,
          refreshToken: latest.refreshToken ?? refreshToken,
        },
      });

      const rotated = response.data.token;
      if (rotated === "Token already used" || rotated === "Token not expired") {
        return token;
      }

      useAuthStore.getState().login(rotated, response.data.refreshToken);
      return rotated;
    });

    isRefreshing = false;
    processQueue(null, newToken);
    return newToken;
  } catch (e) {
    isRefreshing = false;
    processQueue(e, null);
    // Do not log out here — only 440 (handled in the interceptor) ends the session.
    throw e;
  }
};

/**
 * Refresh outside a failed request (the realtime handshake). Shares the in-tab
 * queue and the cross-tab lock with the 401 path. A 440 still ends the session
 * through the response interceptor.
 */
export async function refreshAccessToken(): Promise<string> {
  const { token, refreshToken: stored } = useAuthStore.getState();
  if (!token) throw new Error("Not signed in");
  return (await refreshToken(token, stored ?? "")) as string;
}

axiosInstance.interceptors.response.use(
  async (response) => {
    pageHasAnswered = true;
    return response;
  },
  async (error) => {
    const authState = useAuthStore.getState();
    const originalRequest = error.config;

    if (isAxiosError(error)) {
      await hydrateAxiosErrorBody(error);
    }

    const status = error.response?.status as number | undefined;
    const data = error.response?.data as AiBudgetExceededPayload | undefined;

    if (
      status === AI_BUDGET_EXCEEDED_STATUS ||
      data?.code === AI_BUDGET_EXCEEDED_CODE
    ) {
      error.message = formatAiBudgetExceededMessage(data);
    } else if (status === 400 && data?.message) {
      error.message = data.message;
    }

    // Explicit session-ended signal. A sign-in call's own 440 is just an answer.
    if (status === SESSION_EXPIRED_STATUS && !originalRequest?.skipAuthRefresh) {
      const sent = (error.response?.data as { reason?: string } | undefined)?.reason;
      const reason: SessionEndReason = sent === "revoked" || sent === "locked" ? sent : "expired";
      // A locked account cannot sign back in over the page: only a password
      // reset by email lifts the lock, so go straight to Sign in, which says so.
      if (!pageHasAnswered || !authState.token || reason === "locked") {
        endSession(reason);
        return Promise.reject(error);
      }
      try {
        const token = await waitForReauth(reason);
        // The refresh call itself: answer it with the new pair, so the 401
        // path that asked for it retries its own request as usual.
        if (typeof originalRequest.url === "string" && originalRequest.url.includes("auth/refresh-token")) {
          const { refreshToken: rt } = useAuthStore.getState();
          return { ...error.response, status: 200, data: { token, refreshToken: rt } };
        }
        originalRequest.headers["Authorization"] = `Bearer ${token}`;
        return axios(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }

    // Access token missing/expired — try refresh once. 403 never reaches here.
    if (status === 401 && !originalRequest._retry && !originalRequest.skipAuthRefresh && authState.token) {
      originalRequest._retry = true;
      try {
        const newToken = await refreshToken(
          authState.token,
          authState.refreshToken || "",
        );
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);
